import _ from 'lodash';
import { Filters, FiltersWithoutSearch } from '@common/types';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import { updateQueryParams } from '@utils/route';
import { useTemplateGalleryFilters } from '@components/Hooks/useTemplateGalleryFilters/useTemplateGalleryFilters';
import { useShallowRouter } from '@components/Hooks/useRouter';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';

export type SingleSelectionFilters = keyof Pick<Filters, 'siteType' | 'price'>;

export type MultiSelectionFilters = keyof Pick<
  Filters,
  'features' | 'industries' | 'styles' | 'languages'
>;

export const FILTER_TO_KEY_PREFIX_MAP: Record<
  keyof FiltersWithoutSearch,
  string
> = {
  siteType: 'marketplace.templates-page.site-type-',
  industries: 'marketplace.templates-page.industry-',
  features: 'marketplace.templates-page.feature-',
  languages: 'marketplace.templates-page.language-',
  price: 'marketplace.templates-page.price-type-',
  styles: 'marketplace.templates-page.styles-',
};

export interface FilterTagProps {
  key: string;
  onRemove?: () => void;
}

export const useFilterTags = () => {
  const {
    state: { filters },
  } = useTemplatesContext();
  const { router, navigate } = useShallowRouter();

  const { activeFilterMap, hasActiveFilters } = useTemplateGalleryFilters();

  const { logBiClick } = useTemplatesBi({ is_mobile: false });

  const clearFilterField = (filterType: keyof Filters) => () => {
    logBiClick({
      click_type: 'Remove filter',
      click_data: { filter_name: filters[filterType] },
    });
    navigate(
      updateQueryParams(
        {
          [filterType]: undefined,
        },
        router,
      ),
    );
  };

  const removeSelectionFromMultiFilter =
    (
      filterType: MultiSelectionFilters,
      filterArray: Array<string>,
      selectionToRemove: string,
    ) =>
    () => {
      const _selectedOptions = _.filter(filterArray, (entry) => {
        return entry !== selectionToRemove;
      });
      const updatedFilterString = _selectedOptions.join(',');

      logBiClick({
        click_type: 'Remove filter',
        click_data: { filter_name: selectionToRemove },
      });

      navigate(
        updateQueryParams(
          {
            [filterType]: updatedFilterString,
          },
          router,
        ),
      );
    };

  const getSingleSelectionFilterTag = (filterType: SingleSelectionFilters) => {
    if (activeFilterMap[filterType]) {
      const filterKeyPrefix = FILTER_TO_KEY_PREFIX_MAP[filterType];
      const filterKey = filters[filterType];
      return {
        key: `${filterKeyPrefix}${filterKey}`,
        dataHook: '',
        onRemove: clearFilterField(filterType),
      };
    }
  };

  const getMultiSelectionFilterTags = (filterType: MultiSelectionFilters) => {
    const currentFilter = filters[filterType];
    const tags = _.map(currentFilter, (filterEntry) => {
      const filterKeyPrefix = FILTER_TO_KEY_PREFIX_MAP[filterType];
      return {
        key: `${filterKeyPrefix}${filterEntry}`,
        onRemove: removeSelectionFromMultiFilter(
          filterType,
          filters[filterType],
          filterEntry,
        ),
      };
    });
    return tags;
  };

  const siteTypeTag = getSingleSelectionFilterTag('siteType');

  const priceTag = getSingleSelectionFilterTag('price');

  const industryTags = getMultiSelectionFilterTags('industries');

  const featureTags = getMultiSelectionFilterTags('features');

  const styleTags = getMultiSelectionFilterTags('styles');

  const languagesTags = getMultiSelectionFilterTags('languages');

  const singleSelectionTags: Array<FilterTagProps> = [];

  if (siteTypeTag) {
    singleSelectionTags.push(siteTypeTag);
  }

  if (priceTag) {
    singleSelectionTags.push(priceTag);
  }

  const tags = [
    ...singleSelectionTags,
    ...industryTags,
    ...featureTags,
    ...styleTags,
    ...languagesTags,
  ];

  return {
    tags,
    hasActiveFilters,
  };
};
